import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CloseOutlined } from '@mui/icons-material';
import Button from '../../atoms/Button/Button';
import Input from '../../atoms/Input/Input';
import ConfirmBar from '../../atoms/ConfirmBar/ConfirmBar';
import { COUNTRIES, FRANCE_DEPARTMENTS } from '../../../utils/geoData';
import './UserModal.css';
import { showToast } from '../../atoms/Toast/Toast';
const isWeak = (pwd) =>
  pwd.length < 8 || !/[0-9]/.test(pwd) || !/[^a-zA-Z0-9]/.test(pwd);

const blockInvalidNumberKeys = (e) => {
  if (['-', 'e', 'E', '+', '.'].includes(e.key)) e.preventDefault();
};

const sanitizeNbrMax = (raw) => raw.replace(/[^0-9]/g, '');

const arraysEqualById = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
};

// ── Composant multi-select réutilisable ──────────────────────────
const SupervisorMultiSelect = ({
  adminsList,
  value = [],
  onChange,
  excludeId,
  label,
  optional,
  required,
  showStar, // ← contrôle l'affichage de l'étoile indépendamment de `required`
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (id) => {
    const strId = String(id);
    const isCurrentlySelected = value.includes(strId);

    // --- FIX INTERDICTION DE SUPPRESSION DU DERNIER ---
    if (isCurrentlySelected) {
      if (value.length === 1 && required) {
        return;
      }
      onChange(value.filter((v) => v !== strId));
    } else {
      onChange([...value, strId]);
    }
  };

  const filtered = adminsList.filter((a) => String(a.id) !== String(excludeId));

  const displayLabel = () => {
    if (!value.length) return '— Aucun superviseur —';
    if (value.length === 1) {
      const found = adminsList.find((a) => String(a.id) === value[0]);
      return found ? found.username : '1 superviseur';
    }
    return `${value.length} superviseurs sélectionnés`;
  };

  return (
    <div className="form-group">
      <label>
        {label}
        {optional && <span className="optional-tag">optionnel</span>}
        {showStar && ' *'}
      </label>
      <div className="multi-select-wrapper" ref={ref}>
        <button
          type="button"
          className={[
            'multi-select-trigger',
            open  ? 'multi-select-trigger--open'      : '',
            value.length ? 'multi-select-trigger--has-value' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="multi-select-trigger__label">{displayLabel()}</span>
          <span className="multi-select-trigger__arrow">{open ? '▲' : '▼'}</span>
        </button>

        {open && (
          <div className="multi-select-dropdown">
            {filtered.length === 0 && (
              <div className="multi-select-empty">Aucun admin disponible</div>
            )}
            <label className="multi-select-option multi-select-option--none">
              <input
                type="checkbox"
                checked={value.length === 0}
                onChange={() => onChange([])}
                disabled={required}
              />
              <span>— Aucun superviseur —</span>
            </label>
            {filtered.map((admin) => {
              const strId  = String(admin.id);
              const checked = value.includes(strId);
              const isLastRequired = required && value.length === 1 && checked;

              return (
                <label
                  key={admin.id}
                  className={`multi-select-option${checked ? ' multi-select-option--checked' : ''}${isLastRequired ? ' disabled-checkbox' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(admin.id)}
                    disabled={isLastRequired}
                  />
                  <span className="multi-select-option__name">{admin.username}</span>
                  <span className="multi-select-option__email">{admin.email}</span>
                </label>
              );
            })}
          </div>
        )}

        {value.length > 0 && (
          <div className="multi-select-tags">
            {value.map((id) => {
              const found = adminsList.find((a) => String(a.id) === id);
              if (!found) return null;
              const isLastRequired = required && value.length === 1;
              return (
                <span key={id} className="multi-select-tag">
                  {found.username}
                  <button
                    type="button"
                    className="multi-select-tag__remove"
                    onClick={() => toggle(id)}
                    title={isLastRequired ? "Au moins un superviseur est requis" : "Retirer"}
                    disabled={isLastRequired}
                    style={{ cursor: isLastRequired ? 'not-allowed' : 'pointer', opacity: isLastRequired ? 0.5 : 1 }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────

const UserModal = ({ user, onClose, onSubmit, adminsList = [] }) => {
  const currentUser  = useSelector((state) => state.auth.user);
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isAdmin      = currentUser?.role === 'admin';
  const isEditMode   = !!user;

  // helper unique pour décider d'afficher l'étoile (création uniquement)
  const star = (condition = true) => !isEditMode && condition ? ' *' : '';

  const [formData, setFormData] = useState({
    username: '', email: '', name: '', lastname: '',
    role: 'admin',
    password: '', confirmPassword: '',
    organization: '', adress: '', country: '', city: '',
    active: true,
    is_internal: false, 
    supervised_by_admin_ids: [],
    nbr_max: '',
  });

  const [initialData, setInitialData]     = useState(null);
  const [hasChanges, setHasChanges]       = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // ── NOUVEAU : flux de confirmation en deux étapes ──────────────────
  const [pendingSubmit,  setPendingSubmit]  = useState(null); // { dataToSubmit } | null
  const [pendingConfirm, setPendingConfirm] = useState(null); // { dataToSubmit } | null

  const isFrance = formData.country === 'France';

  useEffect(() => {
    if (user) {
      const userData = {
        username:     user.username     || '',
        email:        user.email        || '',
        name:         user.name         || '',
        lastname:     user.lastname     || '',
        role:         user.role         || 'user',
        organization: user.organization || '',
        adress:       user.adress       || '',
        country:      user.country      || '',
        city:         user.city         || '',
        active:       user.active !== undefined ? user.active : true,
        is_internal:  user.is_internal !== undefined ? user.is_internal : false, // ✅
        password:     '',
        confirmPassword: '',
        supervised_by_admin_ids: (user.supervisor_ids || []).map(String),
        nbr_max: user.nbr_max != null ? String(user.nbr_max) : '',
      };
      setFormData(userData);
      setInitialData(userData);
    }
}, [user]);
  useEffect(() => {
    if (isEditMode && initialData) {
      const changed = Object.keys(formData).some((key) => {
        if (['username', 'email', 'name', 'lastname'].includes(key)) return false;
        if (key === 'password' || key === 'confirmPassword') return formData[key] !== '';
        if (key === 'supervised_by_admin_ids') {
          return !arraysEqualById(formData[key], initialData[key]);
        }
        return formData[key] !== initialData[key];
      });
      setHasChanges(changed);
    }
  }, [formData, initialData, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!isEditMode) {
      if (!formData.password) {
        setPasswordError('Le mot de passe est requis.'); return;
      }
      if (isWeak(formData.password)) {
        setPasswordError('Mot de passe trop faible (8 car. min., 1 chiffre, 1 caractère spécial).'); return;
      }
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('Les mots de passe ne correspondent pas.'); return;
      }
      if (formData.role === 'admin') {
        if (!formData.nbr_max) {
          setPasswordError('Le nombre maximum de créations est obligatoire pour un admin.'); return;
        }
        if (parseInt(formData.nbr_max, 10) < 1) {
          setPasswordError('Le nombre maximum de créations doit être supérieur à 0.'); return;
        }
      }
      if (
        formData.role === 'user' &&
        !formData.supervised_by_admin_ids.length &&
        !showAutoSupervisorNote
      ) {
        setPasswordError('Veuillez sélectionner au moins un admin superviseur.'); return;
      }
    }
    if (isEditMode && formData.role === 'admin' && formData.nbr_max !== '' && parseInt(formData.nbr_max, 10) < 1) {
      setPasswordError('Le nombre maximum de créations doit être supérieur à 0.'); return;
    }
    if (isEditMode && formData.password) {
      if (isWeak(formData.password)) { setPasswordError('Mot de passe trop faible.'); return; }
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('Les mots de passe ne correspondent pas.'); return;
      }
    }

    const { confirmPassword, supervised_by_admin_ids, nbr_max, ...dataToSubmit } = formData;

    if (isEditMode && !dataToSubmit.password) delete dataToSubmit.password;
    if (isEditMode && !isSuperAdmin) delete dataToSubmit.role;

    dataToSubmit.supervised_by_admin_ids = supervised_by_admin_ids.map(Number);

    if (formData.role === 'admin' && nbr_max) {
      const parsed = parseInt(nbr_max, 10);
      if (!isNaN(parsed) && parsed >= 0) dataToSubmit.nbr_max = parsed;
    }

    // ── En édition, on demande d'abord confirmation avant d'envoyer quoi que ce soit ──
    if (isEditMode) {
      setPendingSubmit({ dataToSubmit });
      return;
    }

    // Création : comportement inchangé, pas de flux de confirmation
    try {
      await onSubmit(dataToSubmit);
    } catch (err) {
      setPasswordError(err.message || 'Une erreur est survenue.');
    }
  };

  // ── Étape 1 : confirmation de la modification ───────────────────
  const handleConfirmSubmit = async () => {
    if (!pendingSubmit) return;
    const { dataToSubmit } = pendingSubmit;
    setPendingSubmit(null);

    try {
      const response = await onSubmit(dataToSubmit);

      // Si le backend demande confirmation d'envoi d'email → étape 2
      if (response?.data?.preview) {
        setPendingConfirm({ dataToSubmit });
      }
      // Sinon : onSubmit a déjà fermé la modale via UsersManagement
    } catch (err) {
      setPasswordError(err.message || 'Une erreur est survenue.');
    }
  };

  const handleCancelSubmit = () => {
    setPendingSubmit(null);
  };

  // ── Étape 2 : confirmation de l'envoi d'email ───────────────────
// ── Étape 2 : confirmation de l'envoi d'email ───────────────────
const handleConfirmEmail = async () => {
  if (!pendingConfirm) return;
  try {
    await onSubmit({}, true); // ← body vide, confirm=true
    setPendingConfirm(null);
  } catch (err) {
    setPasswordError(err.message || "Erreur lors de la confirmation de l'envoi.");
    setPendingConfirm(null);
  }
};

  // Si on annule l'envoi d'email, rien n'a été persisté côté backend
  // (le 1er appel n'était qu'un preview) → on ferme simplement
const handleCancelEmail = () => {
  setPendingConfirm(null);
  showToast('Utilisateur modifié', 'success'); // ← toast ici car update déjà fait
  onClose();
};

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'country' && value !== 'France') updated.city = '';
      if (field === 'role') {
        updated.nbr_max = '';
        updated.supervised_by_admin_ids = [];
      }
      return updated;
    });
    if (['password', 'confirmPassword'].includes(field)) setPasswordError('');
  };

  const handleNbrMaxChange = (e) => handleChange('nbr_max', sanitizeNbrMax(e.target.value));
  const handleInputChange  = (e) => handleChange(e.target.name, e.target.value);
  const handleRoleChange   = (newRole) => handleChange('role', newRole);

  // ── Conditions d'affichage ─────────────────────────────────────
  const showAdminSupervisors   = formData.role === 'admin' && (isSuperAdmin || (isAdmin && isEditMode));
  const showUserSupervisors    = formData.role === 'user'  && (isSuperAdmin || (isAdmin && isEditMode));
  const showNbrMax             = formData.role === 'admin';
  const showAutoSupervisorNote = isAdmin && !isEditMode;

  const pwdStrength = (() => {
    const p = formData.password;
    if (!p) return null;
    if (isWeak(p)) return { label: 'Faible', color: '#be1622' };
    return p.length >= 12 && /[A-Z]/.test(p)
      ? { label: 'Fort',  color: '#16a34a' }
      : { label: 'Moyen', color: '#f59e0b' };
  })();

  return (
    // plus de onClick={onClose} sur l'overlay — clic extérieur ne ferme plus le modal
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        {/* ── Header avec croix de fermeture ── */}
        <div className="modal-header-row">
          <h3>{isEditMode ? "Modifier l'utilisateur" : 'Ajouter un utilisateur'}</h3>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            title="Fermer"
          >
            <CloseOutlined />
          </button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Bandeau d'erreur global — visible en haut, pas lié à un champ précis ── */}
          {passwordError && (
            <div className="form-error-banner">
              {passwordError}
            </div>
          )}

          <div className="form-grid">

            {/* ── Identité ── */}
            <div className="form-group">
              <label>Nom d'utilisateur{star()}</label>
              <input
                type="text" value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                disabled={isEditMode} required={!isEditMode}
                className={isEditMode ? 'disabled-field' : ''}
              />
            </div>

            <div className="form-group">
              <label>Email{star()}</label>
              <input
                type="email" value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={isEditMode} required={!isEditMode}
                className={isEditMode ? 'disabled-field' : ''}
              />
            </div>

            <div className="form-group">
              <label>Nom{star()}</label>
              <input
                type="text" value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={isEditMode} required={!isEditMode}
                className={isEditMode ? 'disabled-field' : ''}
              />
            </div>

            <div className="form-group">
              <label>Prénom{star()}</label>
              <input
                type="text" value={formData.lastname}
                onChange={(e) => handleChange('lastname', e.target.value)}
                disabled={isEditMode} required={!isEditMode}
                className={isEditMode ? 'disabled-field' : ''}
              />
            </div>

            {/* ── Mot de passe ── */}
            <div className="form-group">
              <label>Mot de passe{star()}</label>
              <Input
                type="password" name="password" value={formData.password}
                onChangeHandler={handleInputChange} required={!isEditMode}
                additionalClass={`modal-input${passwordError && passwordError.includes('faible') ? ' modal-input--error' : ''}`}
              />
              {pwdStrength && (
                <span className="pwd-strength" style={{ color: pwdStrength.color }}>
                  ● {pwdStrength.label}
                </span>
              )}
              {!isEditMode && !formData.password && (
                <span className="pwd-hint">8 car. min., 1 chiffre, 1 caractère spécial</span>
              )}
            </div>

            <div className="form-group">
              <label>Confirmer le mot de passe{star()}</label>
              <Input
                type="password" name="confirmPassword" value={formData.confirmPassword}
                onChangeHandler={handleInputChange} required={!isEditMode}
                additionalClass={`modal-input${passwordError && passwordError.includes('correspondent') ? ' modal-input--error' : ''}`}
              />
            </div>

              {!isEditMode && (
  <div className="form-group">
    <label>Type de compte{star()}</label>
    <select
      value={formData.is_internal ? 'internal' : 'external'}
      onChange={(e) => handleChange('is_internal', e.target.value === 'internal')}
    >
      <option value="external">Externe</option>
      <option value="internal">Interne</option>
    </select>
  </div>
)}
            {/* ── Rôle — création uniquement ── */}
            {!isEditMode && (
              <div className="form-group">
                <label>Rôle{star()}</label>
                <select value={formData.role} onChange={(e) => handleRoleChange(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="user">Utilisateur</option>
                </select>
              </div>
            )}

            {/* ── Superviseurs rôle ADMIN ── */}
            {showAdminSupervisors && (
              <SupervisorMultiSelect
                adminsList={adminsList}
                value={formData.supervised_by_admin_ids}
                onChange={(ids) => handleChange('supervised_by_admin_ids', ids)}
                excludeId={user?.id}
                label="Admins Superviseurs"
                optional
              />
            )}

            {/* ── Superviseurs rôle USER ── */}
            {showUserSupervisors && (
              <SupervisorMultiSelect
                adminsList={adminsList}
                value={formData.supervised_by_admin_ids}
                onChange={(ids) => handleChange('supervised_by_admin_ids', ids)}
                excludeId={user?.id}
                label="Admin(s) Superviseur(s)"
                required          // règle métier conservée (dernier superviseur non retirable) même en édition
                showStar={!isEditMode} // étoile visible uniquement à la création
              />
            )}

            {/* ── nbr_max — admin ── */}
            {showNbrMax && (
              <div className="form-group">
                <label>Nb. max d'utilisateurs créables{star()}</label>
                <input
                  type="number" min="1" step="1"
                  value={formData.nbr_max}
                  onChange={handleNbrMaxChange}
                  onKeyDown={blockInvalidNumberKeys}
                  required={!isEditMode}
                />
              </div>
            )}

            {/* ── Note auto-superviseur ── */}
            {showAutoSupervisorNote && (
              <div className="form-group form-group--full">
                <div className="info-note">
                  <span className="info-note__icon">👤</span>
                  <span className="info-note__text">
                    {formData.role === 'admin'
                      ? 'Vous serez automatiquement le superviseur de cet admin.'
                      : 'Vous serez automatiquement le superviseur de cet utilisateur.'}
                  </span>
                </div>
              </div>
            )}

            {/* ── Statut / Organisation / Adresse / Pays / Ville ── */}
            <div className="form-group">
              <label>Statut{star()}</label>
              <select
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) => handleChange('active', e.target.value === 'active')}
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>

            <div className="form-group">
              <label>Organisation{star()}</label>
              <input
                type="text" value={formData.organization}
                onChange={(e) => handleChange('organization', e.target.value)}
                required={!isEditMode}
              />
            </div>

            <div className="form-group">
              <label>Adresse{star()}</label>
              <input
                type="text" value={formData.adress}
                onChange={(e) => handleChange('adress', e.target.value)}
                required={!isEditMode}
              />
            </div>

            <div className="form-group">
              <label>Pays{star()}</label>
              <select
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                required={!isEditMode}
              >
                <option value="">— Sélectionner un pays —</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>{isFrance ? 'Département' : 'Ville'}{star()}</label>
              {isFrance ? (
                <select
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  required={!isEditMode}
                >
                  <option value="">— Sélectionner un département —</option>
                  {FRANCE_DEPARTMENTS.map((d) => (
                    <option key={d.code} value={`${d.code} - ${d.name}`}>{d.code} — {d.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text" value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder={formData.country ? 'Saisir une ville' : "Sélectionnez d'abord un pays"}
                  disabled={!formData.country}
                  className={!formData.country ? 'disabled-field' : ''}
                  required={!isEditMode}
                />
              )}
            </div>

          </div>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose} label="Annuler" />
            <Button
              type="submit" variant="primary"
              label={isEditMode ? 'Enregistrer les modifications' : 'Créer'}
              disabled={isEditMode && !hasChanges}
            />
          </div>
        </form>
      </div>

      {pendingSubmit && (
        <ConfirmBar
          message="Confirmer la modification de l'utilisateur"
          highlight={formData.username}
          onConfirm={handleConfirmSubmit}
          onCancel={handleCancelSubmit}
        />
      )}

      {pendingConfirm && (
        <ConfirmBar
          message="Envoyer un email de notification à"
          highlight={formData.username}
          onConfirm={handleConfirmEmail}
          onCancel={handleCancelEmail}
          confirmLabel="Oui, envoyer l'email"
          cancelLabel="Non, ne pas envoyer"
        />
      )}
    </div>
  );
};

export default UserModal;